import type { PageSeo } from "@/lib/api/seo.functions";

type MetaEntry = Record<string, string>;
type LinkEntry = Record<string, string>;
type ScriptEntry = Record<string, string>;
type HeadShape = { meta?: MetaEntry[]; links?: LinkEntry[]; scripts?: ScriptEntry[] };

/**
 * Merge a route's default head() output with an admin-controlled PageSeo row.
 * DB values override defaults. Pass `null` when there is no override.
 */
export function mergeSeo(defaults: HeadShape, override: PageSeo | null | undefined): HeadShape {
  if (!override) return defaults;

  const meta: MetaEntry[] = [...(defaults.meta ?? [])];

  const setMeta = (matcher: (m: MetaEntry) => boolean, entry: MetaEntry) => {
    const idx = meta.findIndex(matcher);
    if (idx >= 0) meta[idx] = entry;
    else meta.push(entry);
  };

  if (override.title) {
    setMeta((m) => typeof m.title === "string", { title: override.title });
    setMeta((m) => m.property === "og:title", { property: "og:title", content: override.title });
    setMeta((m) => m.name === "twitter:title", { name: "twitter:title", content: override.title });
  }
  if (override.description) {
    setMeta((m) => m.name === "description", { name: "description", content: override.description });
    setMeta((m) => m.property === "og:description", { property: "og:description", content: override.description });
    setMeta((m) => m.name === "twitter:description", { name: "twitter:description", content: override.description });
  }
  if (override.keywords) {
    setMeta((m) => m.name === "keywords", { name: "keywords", content: override.keywords });
  }
  if (override.og_image) {
    setMeta((m) => m.property === "og:image", { property: "og:image", content: override.og_image });
    setMeta((m) => m.name === "twitter:image", { name: "twitter:image", content: override.og_image });
  }
  if (override.og_type) {
    setMeta((m) => m.property === "og:type", { property: "og:type", content: override.og_type });
  }
  if (override.robots) {
    setMeta((m) => m.name === "robots", { name: "robots", content: override.robots });
  }

  const links: LinkEntry[] = [...(defaults.links ?? [])];
  if (override.canonical) {
    const idx = links.findIndex((l) => l.rel === "canonical");
    const entry = { rel: "canonical", href: override.canonical };
    if (idx >= 0) links[idx] = entry;
    else links.push(entry);
  }

  const scripts: ScriptEntry[] = [...(defaults.scripts ?? [])];
  if (override.json_ld) {
    scripts.push({
      type: "application/ld+json",
      children: JSON.stringify(override.json_ld),
    });
  }

  return { meta, links, scripts };
}
